/**
 * ImpersonationContext (#1137 Task 3.1).
 *
 * React Context provider that holds the active impersonation grant in
 * sessionStorage so it survives full-page reloads but vanishes when the
 * tab closes. Companion to the axios request interceptor (Task 3.2)
 * which actually attaches the X-Impersonation-Grant header to outbound
 * API calls — this context only owns lifecycle (start, stop, hydrate).
 *
 * Shape of `impersonation`:
 *   {
 *     grant_token, grant_id,
 *     target_user_id, target_email, target_role,
 *     scope, workspace_id, workspace_name,
 *     expires_at,
 *     origin_path,   // window.location.pathname at the time of start
 *   }
 *
 * The origin_path is captured so stopImpersonation can return the actor
 * to where they came from (e.g. /admin/users) instead of the
 * impersonated user's view of the dashboard.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../App';

const STORAGE_KEY = 'fray_impersonation';

const ImpersonationContext = createContext(null);

export function ImpersonationProvider({ children }) {
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      // Malformed JSON or sessionStorage unavailable — boot clean.
      return null;
    }
  });

  // Mirror state to sessionStorage so a hard reload re-hydrates the
  // active grant. Cleanup happens here too: setting state to null wipes
  // the key.
  useEffect(() => {
    try {
      if (impersonation) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(impersonation));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) { /* sessionStorage unavailable; no-op */ }
  }, [impersonation]);

  // #1165: client-side expiry timer. The backend's auth consumer silently
  // falls back to the real actor when a JWT expires (no 403 emitted) so
  // the original frontend interceptor's "clear-on-403" path never
  // fires for natural TTL expiry. Without this timer the banner sits
  // visible saying "EXPIRES IN 0M" while every API call returns the
  // actor's data — confusing UX.
  //
  // #1165 followup: also navigate back to the origin path (same as
  // manual STOP) on timer fire. Without navigation, cached page
  // content from the impersonated user's view stays visible even
  // though the API auth has reverted to the real actor — operator
  // sees stale data attributed to "still impersonating" mentally.
  // Calling stopImpersonation with skipBackendRevoke=true gives us
  // the navigation + state-clear without a pointless backend revoke
  // call (the grant is already expired naturally).
  useEffect(() => {
    if (!impersonation?.expires_at) return undefined;
    const expiresMs = new Date(impersonation.expires_at).getTime();
    const remainingMs = expiresMs - Date.now();
    if (remainingMs <= 0) {
      // Already expired (e.g. session-storage hydrate after a long
      // inactive period). Trigger the same flow as manual STOP minus
      // the backend revoke — fire-and-forget since stopImpersonation
      // is async and useEffect can't await.
      stopImpersonation({ skipBackendRevoke: true });
      return undefined;
    }
    const handle = setTimeout(() => {
      stopImpersonation({ skipBackendRevoke: true });
    }, remainingMs);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonation]);

  const startImpersonation = (payload) => {
    const next = { ...payload, origin_path: window.location.pathname };
    setImpersonation(next);
    // Drop the actor onto the impersonated user's home so role-gated
    // routes re-evaluate cleanly.
    window.location.href = '/';
  };

  const stopImpersonation = async ({ skipBackendRevoke = false } = {}) => {
    const current = impersonation;
    if (current && !skipBackendRevoke) {
      try {
        // #1166: use the configured API instance (with Bearer auth) instead
        // of raw axios. The impersonation interceptor at
        // lib/impersonationInterceptor.js:27 has REVOKE_URL_RE which
        // already skips attaching the X-Impersonation-Grant header on
        // revoke URLs (B3 deadlock fix preserved). Raw axios was over-
        // cautious — it had no auth interceptor either, so the call went
        // out without the Bearer token, backend returned 401, and the
        // failure was silently swallowed below. Grant `revoked_at` stayed
        // NULL despite STOP clicks.
        await API.post(`/api/impersonate/grant/${current.grant_id}/revoke`);
      } catch (_) {
        // Network failure — local clear still happens. Backend grant
        // will expire naturally; auditors can still see start + use
        // events even without an explicit revoke event.
      }
    }
    setImpersonation(null);
    const back = current?.origin_path
      || (current?.scope === 'workspace'
            ? `/enterprise-admin/${current.workspace_id}`
            : '/admin');
    window.location.href = back;
  };

  return (
    <ImpersonationContext.Provider
      value={{ impersonation, startImpersonation, stopImpersonation }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = () => useContext(ImpersonationContext);
